/**
 * Formula group helpers — canonical expression + mode-switch body
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  ensureFormulaBlocksBody,
  type FormulaBlockNode,
  type FormulaBlocksBody,
  type FormulaGroup,
  type FormulaGroupMode,
  type FormulaSubMetric,
} from '@trafficpulse/shared';

import { rebalance } from '../../hooks/auto-balanced-sliders.math';
import { serializeSliders } from './sliders';
import { appendSubrefToMaster, makeSubformula, serializeMaster } from './subformulas';

type SlidersToAst = (metrics: FormulaSubMetric[]) => FormulaBlockNode | null;
type AstToSliders = (
  node: FormulaBlockNode | null,
) => { metrics: FormulaSubMetric[] } | null;

/** Canonical expression text used by the backend evaluator. */
export function recomputeGroupExpression(group: FormulaGroup): string {
  if (group.mode === 'sliders') return serializeSliders(group.sliders);
  if (group.mode === 'blocks') return group.blocks?.masterExpression ?? '';
  return group.expression;
}

/** Best-effort patch to switch a group's authoring mode. */
export function switchModeBody(
  group: FormulaGroup,
  next: FormulaGroupMode,
  slidersToAst: SlidersToAst,
  astToSliders: AstToSliders,
): Partial<FormulaGroup> {
  if (next === group.mode) return {};
  if (next === 'blocks') return slidersToBlocks(group, slidersToAst);
  return blocksToSliders(group, astToSliders);
}

function slidersToBlocks(
  group: FormulaGroup,
  slidersToAst: SlidersToAst,
): Partial<FormulaGroup> {
  const ast = slidersToAst(group.sliders?.metrics ?? []);
  if (!ast) {
    return {
      mode: 'blocks',
      blocks: ensureFormulaBlocksBody(group.blocks ?? null, group.name || 'Subformula 1'),
    };
  }
  const sub = makeSubformula(group.name || 'Subformula 1');
  sub.ast = ast;
  sub.expression = serializeMaster(ast);
  const master = appendSubrefToMaster(null, sub.id);
  return {
    mode: 'blocks',
    blocks: {
      subformulas: [sub],
      master,
      masterExpression: serializeMaster(master),
    },
  };
}

function blocksToSliders(
  group: FormulaGroup,
  astToSliders: AstToSliders,
): Partial<FormulaGroup> {
  const blocks = ensureFormulaBlocksBody(group.blocks ?? null, group.name || 'Subformula 1');
  const slidersOut = blocksAsSliders(blocks, astToSliders);
  if (slidersOut) {
    return { mode: 'sliders', sliders: { metrics: slidersOut.metrics } };
  }
  if (typeof window !== 'undefined') {
    const ok = window.confirm(
      'Switching to Sliders will discard the current subformulas. Continue?',
    );
    if (!ok) return {};
  }
  return { mode: 'sliders', sliders: group.sliders ?? { metrics: [] } };
}

/**
 * Weighted-sum detection for the blocks -> sliders round-trip. Only a single
 * subformula whose AST is a weighted sum of metrics is expressible as
 * sliders; several subformulas are always lossy (confirm before discarding).
 */
function blocksAsSliders(
  blocks: FormulaBlocksBody,
  astToSliders: AstToSliders,
): { metrics: FormulaSubMetric[] } | null {
  if (blocks.subformulas.length === 0) return { metrics: [] };
  if (blocks.subformulas.length > 1) return null;
  return astToSliders(blocks.subformulas[0].ast ?? null);
}

/**
 * Seed equal shares for `newId` whenever the existing master weights all sum
 * to 0 — prevents the persisted FormulaConfig.groupWeights from going out the
 * door at 0, which would collapse the backend rawScore to 0 across the board.
 */
export function seedEqualShareIfEmpty(
  existing: Record<string, number>,
  groups: FormulaGroup[],
  newId: string,
): Record<string, number> {
  const total = groups.reduce(
    (sum, g) => sum + Math.max(0, existing[g.id] ?? 0),
    0,
  );
  if (total > 0) return { ...existing, [newId]: 0 };
  const share = 100 / groups.length;
  return Object.fromEntries(groups.map((g) => [g.id, share]));
}

/** Round group weights so they always sum to exactly 100 across `groups`. */
export function rebalanceGroupWeights(
  weights: Record<string, number>,
  groups: FormulaGroup[],
): Record<string, number> {
  if (groups.length === 0) return {};
  const seeded: Record<string, number> = {};
  for (const g of groups) seeded[g.id] = weights[g.id] ?? 0;
  return rebalance(seeded, null);
}
